import React from "react";
import { Pressable, Text, View, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { fontFamily } from "../../../constants/fonts";
import { colors } from "../../../constants/theme";

/** Red dot + count — reuse on map pill and web sidebar Passes. */
export function PassesAvailabilityBadge({ count = 0 }) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (
    <View style={badgeStyles.wrap} accessibilityElementsHidden>
      <Text style={badgeStyles.text}>{n > 99 ? "99+" : String(n)}</Text>
    </View>
  );
}

/**
 * Map explore row (native + web mobile): Passes pill with optional badge
 * for how many passes are listed for today’s weekday (same rules as Passes tab).
 *
 * @param {boolean} [fillContainer] When true, stretch to parent width (native map row uses flex + minWidth: 0).
 *   Omit on web mobile row-only layout — `width: "100%"` there resolves to the full row and can shrink the pill to a blank chip.
 */
export default function PassesExplorePill({
  onPress,
  isActive = false,
  availableTodayCount = 0,
  style,
  fillContainer = false,
}) {
  const hasBadge = Number.isFinite(availableTodayCount) && availableTodayCount > 0;
  return (
    <View style={[styles.wrap, fillContainer && styles.wrapFill, style]}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={
          hasBadge
            ? `Passes, ${availableTodayCount} available to redeem now`
            : "Passes"
        }
        style={({ pressed }) => [
          styles.button,
          fillContainer && styles.buttonFill,
          isActive ? styles.buttonActive : styles.buttonIdle,
          pressed && styles.buttonPressed,
        ]}
      >
        <Ionicons
          name="ticket-outline"
          size={22}
          color={isActive ? "#FFFFFF" : "#6B4C3B"}
          style={styles.icon}
        />
        <Text
          style={[styles.label, isActive && styles.labelActive]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          Passes
        </Text>
      </Pressable>
      <PassesAvailabilityBadge count={availableTodayCount} />
    </View>
  );
}

const badgeStyles = StyleSheet.create({
  wrap: {
    position: "absolute",
    top: 0,
    right: 0,
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 7,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#DC2626",
    borderWidth: 2.5,
    borderColor: colors.white,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 3,
    elevation: 6,
  },
  text: {
    fontSize: 12,
    lineHeight: 14,
    color: colors.white,
    fontFamily: fontFamily.abhayaBold,
    includeFontPadding: false,
    fontVariant: ["tabular-nums"],
  },
});

const BADGE_INSET = 12;

const styles = StyleSheet.create({
  wrap: {
    position: "relative",
    flexShrink: 0,
    overflow: "visible",
    paddingTop: BADGE_INSET,
    paddingRight: BADGE_INSET,
  },
  wrapFill: {
    width: "100%",
    minWidth: 0,
  },
  buttonFill: {
    width: "100%",
  },
  button: {
    height: 44,
    borderRadius: 999,
    paddingHorizontal: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  buttonIdle: {
    backgroundColor: "#FFFFFF",
    borderColor: colors.borderLight,
  },
  buttonActive: {
    backgroundColor: "#6B4C3B",
    borderColor: "#6B4C3B",
  },
  buttonPressed: {
    opacity: 0.92,
  },
  icon: {
    flexShrink: 0,
  },
  label: {
    marginLeft: 4,
    color: "#6B4C3B",
    fontFamily: fontFamily.abhayaBold,
    fontSize: 15,
    lineHeight: 20,
    includeFontPadding: false,
    flexShrink: 1,
    minWidth: 0,
  },
  labelActive: {
    color: "#fff",
  },
});
